// src/app/api/fellowsApi.ts
import { baseApi } from "./baseApi";
import type { QuarterKey } from "./programmeApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

export type FellowsStatus =
  | "nominated"
  | "shortlisted"
  | "selected"
  | "declined"
  | "completed";

export interface FellowReport {
  title?:       string;
  body?:        string;
  excerpt?:     string;
  keyLesson?:   string;
  submittedAt?: string;
  publishedAt?: string;
  isPublished:  boolean;
}

// A Legacy Fellow record as returned to mentees (public fields only)
export interface FellowRecord {
  id:                  string;
  mentee: {
    firstName:    string;
    lastName:     string;
    region?:      string;
    ministryRole?: string;
    avatar?:      string;
  };
  quarter:             QuarterKey;
  cycle?:              string;
  status:              FellowsStatus;
  nominatedAt:         string;
  nominationComment?:  string;
  nominatedBy?:        { firstName: string; lastName: string };
  selectedAt?:         string;
  shadowingStartDate?: string;
  shadowingEndDate?:   string;
  shadowingLocation?:  string;
  report:              FellowReport | null;
  createdAt:           string;
  updatedAt:           string;
}

// Eligibility criterion item returned by GET /api/fellows/my-status
export interface EligibilityCriterion {
  key:   string;
  label: string;
  met:   boolean;
  value: string;
}

// Nomination summary item inside my-status
export interface NominationSummary {
  quarter:         QuarterKey;
  status:          FellowsStatus;
  nominatedAt:     string;
  selectedAt?:     string;
  shadowingStart?: string;
  reportPublished: boolean;
}

export interface FellowsStatus_My {
  eligible:        boolean;
  eligibleQuarter: QuarterKey | null;
  currentTier:     string;
  criteria:        EligibilityCriterion[];
  metCount:        number;
  totalCriteria:   number;
  nominations:     NominationSummary[];
}

interface PublishedFellowsResponse {
  success: boolean;
  message: string;
  count:   number;
  data:    FellowRecord[];
}

interface FellowItemResponse {
  success: boolean;
  message: string;
  data:    FellowRecord;
}

interface MyStatusResponse {
  success: boolean;
  message: string;
  data:    FellowsStatus_My;
}

interface NominationsListResponse {
  success: boolean;
  message: string;
  count:   number;
  data:    FellowRecord[];  // includes private fields for admin
}

// interface MessageResponse {
//   success: boolean;
//   message: string;
// }

/* ─────────────────────────────────────────────────────────────────
   API
───────────────────────────────────────────────────────────────── */

export const fellowsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    // GET /api/fellows/published — published cohort reports for the Fellows tab
    getPublishedFellows: builder.query<
      PublishedFellowsResponse,
      { quarter?: QuarterKey }
    >({
      query:        (params) => ({ url: "/fellows/published", params }),
      providesTags: [{ type: "Fellows", id: "PUBLISHED" }],
    }),

    // GET /api/fellows/my-status — current mentee's eligibility + nomination history
    getMyFellowsStatus: builder.query<MyStatusResponse, void>({
      query:        () => "/fellows/my-status",
      providesTags: [{ type: "FellowsStatus", id: "CURRENT" }],
    }),

    // ── Regional Rep ─────────────────────────────────────────────

    // POST /api/fellows/nominate — Rep nominates a mentee from their region
    nominateFellow: builder.mutation<
      FellowItemResponse,
      { menteeId: string; quarter: QuarterKey; cycle?: string; nominationComment?: string }
    >({
      query: (body) => ({ url: "/fellows/nominate", method: "POST", body }),
      invalidatesTags: [
        { type: "FellowsNominations", id: "LIST" },
        { type: "FellowsStatus",      id: "CURRENT" },
      ],
    }),

    // ── Committee Chair / Admin ───────────────────────────────────

    // GET /api/fellows/nominations — admin: all nominations for a quarter
    getNominations: builder.query<
      NominationsListResponse,
      { quarter?: QuarterKey; status?: FellowsStatus }
    >({
      query:        (params) => ({ url: "/fellows/nominations", params }),
      providesTags: [{ type: "FellowsNominations", id: "LIST" }],
    }),

    // PATCH /api/fellows/:id/select — confirm a fellow + add shadowing dates
    selectFellow: builder.mutation<
      FellowItemResponse,
      {
        id:                  string;
        shadowingStartDate?: string;
        shadowingEndDate?:   string;
        shadowingLocation?:  string;
        logisticsNotes?:     string;
      }
    >({
      query: ({ id, ...body }) => ({
        url:    `/fellows/${id}/select`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        { type: "FellowsNominations", id: "LIST" },
        { type: "Fellows",            id: "PUBLISHED" },
        { type: "FellowsStatus",      id: "CURRENT" },
      ],
    }),

    // PATCH /api/fellows/:id/decline — decline a nomination
    declineFellow: builder.mutation<
      FellowItemResponse,
      { id: string; declineReason?: string }
    >({
      query: ({ id, ...body }) => ({
        url:    `/fellows/${id}/decline`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        { type: "FellowsNominations", id: "LIST" },
        { type: "FellowsStatus",      id: "CURRENT" },
      ],
    }),

    // ── Report submission & publication ──────────────────────────

    // PATCH /api/fellows/:id/report — fellow saves draft or admin publishes
    updateFellowReport: builder.mutation<
      FellowItemResponse,
      {
        id:          string;
        title?:      string;
        body?:       string;
        excerpt?:    string;
        keyLesson?:  string;
        isPublished?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({
        url:    `/fellows/${id}/report`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Fellows",            id: "PUBLISHED" },
        { type: "FellowsNominations", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetPublishedFellowsQuery,
  useGetMyFellowsStatusQuery,
  useNominateFellowMutation,
  useGetNominationsQuery,
  useSelectFellowMutation,
  useDeclineFellowMutation,
  useUpdateFellowReportMutation,
} = fellowsApi;